// Tree rules for the master-suite builder — the front-end mirror of the backend's
// MasterSuiteTree. A step's `parentId` holds its parent's `stepReference` (null = the
// step starts a branch). There is no fan-in: a step waits on at most one parent.

/** Children keyed by parent stepReference, in list order. Roots are under "". */
const childrenByParent = (rows = []) => {
  const known = new Set(rows.map((r) => r.stepReference).filter(Boolean));
  const map = new Map();
  rows.forEach((r) => {
    const p = r.parentId && known.has(r.parentId) ? r.parentId : "";
    if (!map.has(p)) map.set(p, []);
    map.get(p).push(r);
  });
  return map;
};

/**
 * Every step below `ref`, excluding `ref` itself.
 *
 * Used to keep a step out of its own subtree's parent picker: if a step could be
 * re-parented under its own descendant the suite would contain a cycle, and the runner
 * would simply never schedule any of them. Preventing it in the picker means the invalid
 * state can't be reached, rather than being caught after the fact.
 */
export function descendantsOf(rows = [], ref) {
  const byParent = childrenByParent(rows);
  const out = new Set();
  const walk = (r) => {
    (byParent.get(r) || []).forEach((child) => {
      if (child.stepReference && !out.has(child.stepReference)) {
        out.add(child.stepReference);
        walk(child.stepReference);
      }
    });
  };
  walk(ref);
  return out;
}

/** True once ANY step has a parent — i.e. the suite's shape is authored, not implied. */
export const hasExplicitParents = (rows = []) =>
  rows.some((r) => !!r.parentId);

/**
 * Makes the implied chain explicit.
 *
 * While every step has a null parent, the backend reads the suite as a linear chain by
 * sequence. The moment ONE step gets a parent that stops being true — the remaining
 * null-parent steps would each become a ROOT and all start at once, which is emphatically
 * not what someone means by "make step C run after step A".
 *
 * So before the first parent is set, write the chain down: each step's parent becomes the
 * one before it. The suite behaves exactly as it did, and every later edit is a change to
 * a shape the user can actually see.
 */
export function ensureExplicitParents(rows = []) {
  if (rows.length < 2 || hasExplicitParents(rows)) return rows;
  return rows.map((r, i) =>
    i === 0 ? { ...r, parentId: null } : { ...r, parentId: rows[i - 1].stepReference || null },
  );
}

/**
 * Steps that may be chosen as `record`'s parent: anything named, except the step itself
 * and anything beneath it.
 */
export function parentOptionsFor(rows = [], record) {
  if (!record) return [];
  const banned = descendantsOf(rows, record.stepReference);
  return rows.filter(
    (r) =>
      r.key !== record.key &&
      !!r.stepReference &&
      !banned.has(r.stepReference),
  );
}

/**
 * The chain of steps `record` waits on, outermost first.
 *
 * This is the ONLY set of steps whose output `record` may read. A step that reads "from
 * previous steps" resolves its producer by name at run time, and with branches running
 * concurrently a step on a SIBLING branch may or may not have finished yet — the same
 * suite would then resolve differently from one run to the next. Ancestors are the steps
 * that are guaranteed to have completed, because the runner only schedules a step once
 * its parent's future has completed.
 *
 * ensureExplicitParents runs first, so for a linear suite this is every step BEFORE this
 * one. Note that is narrower than the list the parameters modal used to offer: it also
 * included steps that come AFTER, which could never have worked — their output does not
 * exist yet when this step runs. Narrowing surfaces that rather than causing it.
 */
export function ancestorsOf(rows = [], record) {
  if (!record) return [];
  const resolved = ensureExplicitParents(rows);
  const byRef = new Map();
  resolved.forEach((r) => {
    if (r.stepReference) byRef.set(r.stepReference, r);
  });

  const me = resolved.find((r) => r.key === record.key);
  const chain = [];
  // Seeded with this step so a malformed suite whose links form a cycle can neither
  // loop forever nor offer the step its own output. This runs on every render of the
  // parameters modal, so it has to be cheap and total.
  const seen = new Set([me?.stepReference]);
  let cursor = me?.parentId ? byRef.get(me.parentId) : null;
  while (cursor && !seen.has(cursor.stepReference)) {
    seen.add(cursor.stepReference);
    chain.push(cursor);
    cursor = cursor.parentId ? byRef.get(cursor.parentId) : null;
  }
  return chain.reverse();
}

/**
 * Re-point children after their parent is RENAMED.
 *
 * parentId holds a stepReference, so a rename that doesn't do this orphans every child:
 * they become roots and start immediately instead of after their parent. Shared by both
 * builders — the New workspace and the Edit table — because getting it wrong in either
 * one produces the same silent, hard-to-spot breakage.
 */
export function relinkOnRename(rows = [], oldRef, newRef, selfKey) {
  if (!oldRef) return rows;
  return rows.map((r) => {
    // NEVER re-point the row being renamed. Links are held by NAME, so the moment a step's
    // name matches its own parent's — which happens mid-word any time a name passes through
    // its parent's as a prefix, "test" → "test 2" — this function cannot tell "my parent"
    // from "me". Without this guard a step's own parent pointer follows its own rename and
    // the step ends up welded to itself, permanently.
    if (selfKey != null && r.key === selfKey) return r;
    return r.parentId && r.parentId === oldRef
      ? { ...r, parentId: newRef || null }
      : r;
  });
}

/**
 * Drops a `parentId` that points at the row's own name.
 *
 * Repairs suites already saved with the corruption described above. A self-parent is never
 * meaningful — the runner would schedule the step only after itself finishes — and the
 * canvas merely tolerates one, so nothing else heals it.
 */
export function healSelfParents(rows = []) {
  return rows.map((r) =>
    r.parentId && r.parentId === r.stepReference ? { ...r, parentId: null } : r,
  );
}

/**
 * Splice a DELETED step out of the tree: its children adopt its parent, so the chain
 * closes up rather than leaving links pointing at a step that no longer exists.
 */
export function relinkOnDelete(rows = [], removed) {
  if (!removed?.stepReference) return rows;
  return rows.map((r) =>
    r.parentId === removed.stepReference
      ? { ...r, parentId: removed.parentId ?? null }
      : r,
  );
}

/**
 * Steps that start at the same moment as `record` — same parent, so they run in parallel.
 * Surfaced in the drawer because "runs after X" alone doesn't tell you that something
 * else also runs after X.
 */
export function siblingsOf(rows = [], record) {
  if (!record) return [];
  const mine = record.parentId || null;
  return rows.filter(
    (r) => r.key !== record.key && (r.parentId || null) === mine && !!r.stepReference,
  );
}
